export interface BoxDimensions {
    length: number
    width: number
    height: number
}

export interface Dieline {
    width: number
    height: number
    columns: { size: number, label: string }[]
    rows: { size: number, label: string }[]
}

export type LayoutMode = 'auto' | 'horizontal' | 'vertical'

export interface FramePlacement {
    x: number
    y: number
    rotated: boolean
}

export interface PlanoLayout {
    count: number
    horizontalCount: number
    verticalCount: number
    cellWidth: number
    cellHeight: number
    placements: FramePlacement[]
    isExhaustive: boolean
}

const UNITS_PER_CM = 100
const EXHAUSTIVE_SEARCH_LIMIT = 400
const MAX_CUT_POSITIONS = 400

const toUnits = (value: number) => Math.round(value * UNITS_PER_CM)
const toCm = (units: number) => units / UNITS_PER_CM

const isPositive = (value: number) => Number.isFinite(value) && value > 0

export const validateBoxDimensions = (box: BoxDimensions): string[] => {
    const errors: string[] = []

    if (!isPositive(box.length)) errors.push('Panjang kemasan harus lebih dari 0 cm.')
    if (!isPositive(box.width)) errors.push('Lebar kemasan harus lebih dari 0 cm.')
    if (!isPositive(box.height)) errors.push('Tinggi kemasan harus lebih dari 0 cm.')

    return errors
}

export const buildDieline = (box: BoxDimensions): Dieline | null => {
    if (validateBoxDimensions(box).length) {
        return null
    }

    const { length: body, width: panel, height: side } = box

    const columns = [
        { size: side, label: 'Samping' },
        { size: panel, label: 'Panel' },
        { size: side, label: 'Samping' },
        { size: panel, label: 'Panel' },
        { size: side, label: 'Lem' },
    ]

    const rows = [
        { size: side, label: 'Flap atas' },
        { size: body, label: 'Badan' },
        { size: side, label: 'Flap bawah' },
    ]

    return {
        width: columns.reduce((total, column) => total + column.size, 0),
        height: rows.reduce((total, row) => total + row.size, 0),
        columns,
        rows,
    }
}

interface Orientation {
    width: number
    height: number
    rotated: boolean
}

type LayoutNode =
    | { count: number, kind: 'empty' }
    | { count: number, kind: 'grid', orientation: Orientation }
    | { count: number, kind: 'vertical-cut' | 'horizontal-cut', at: number }

const collectCutPositions = (limit: number, first: number, second: number, isExhaustive: boolean): number[] => {
    const positions = new Set<number>([0])

    if (isExhaustive) {
        for (let i = 0; i * first <= limit; i++) {
            for (let j = 0; i * first + j * second <= limit; j++) {
                positions.add(i * first + j * second)
            }
        }
    } else {
        for (let i = 0; i * first <= limit; i++) positions.add(i * first)
        for (let j = 0; j * second <= limit; j++) positions.add(j * second)
    }

    return [...positions].sort((left, right) => left - right)
}

const floorToPosition = (positions: number[], value: number): number => {
    let low = 0
    let high = positions.length - 1
    while (low < high) {
        const middle = Math.ceil((low + high) / 2)
        if (positions[middle] <= value) {
            low = middle
        } else {
            high = middle - 1
        }
    }

    return positions[low]
}

export const computePlanoLayout = (
    planoWidth: number,
    planoHeight: number,
    frameWidth: number,
    frameHeight: number,
    safeMargin: number,
    mode: LayoutMode = 'auto',
): PlanoLayout => {
    const cellWidth = toUnits(frameWidth + 2 * safeMargin)
    const cellHeight = toUnits(frameHeight + 2 * safeMargin)
    const sheetWidth = toUnits(planoWidth)
    const sheetHeight = toUnits(planoHeight)

    const emptyLayout: PlanoLayout = {
        count: 0,
        horizontalCount: 0,
        verticalCount: 0,
        cellWidth: toCm(cellWidth),
        cellHeight: toCm(cellHeight),
        placements: [],
        isExhaustive: true,
    }

    if (![cellWidth, cellHeight, sheetWidth, sheetHeight].every((value) => value > 0)) {
        return emptyLayout
    }

    const orientations: Orientation[] = []
    if (mode !== 'vertical') orientations.push({ width: cellWidth, height: cellHeight, rotated: false })
    if (mode !== 'horizontal' && (mode === 'vertical' || cellWidth !== cellHeight)) {
        orientations.push({ width: cellHeight, height: cellWidth, rotated: true })
    }

    const longestSide = Math.max(sheetWidth, sheetHeight)
    const isExhaustive = (longestSide / cellWidth) * (longestSide / cellHeight) <= EXHAUSTIVE_SEARCH_LIMIT
    const cutPositions = collectCutPositions(longestSide, cellWidth, cellHeight, isExhaustive)
    const canCut = cutPositions.length <= MAX_CUT_POSITIONS

    const memo = new Map<string, LayoutNode>()

    const solve = (rawWidth: number, rawHeight: number): LayoutNode => {
        const width = floorToPosition(cutPositions, rawWidth)
        const height = floorToPosition(cutPositions, rawHeight)
        const key = `${width}|${height}`
        const cached = memo.get(key)
        if (cached) return cached

        let best: LayoutNode = { count: 0, kind: 'empty' }
        const upperBound = Math.floor((width * height) / (cellWidth * cellHeight))

        for (const orientation of orientations) {
            if (orientation.width <= width && orientation.height <= height) {
                const count = Math.floor(width / orientation.width) * Math.floor(height / orientation.height)
                if (count > best.count) best = { count, kind: 'grid', orientation }
            }
        }

        for (const at of canCut ? cutPositions : []) {
            if (best.count >= upperBound || at > width / 2) break
            if (at === 0) continue
            const count = solve(at, height).count + solve(width - at, height).count
            if (count > best.count) best = { count, kind: 'vertical-cut', at }
        }

        for (const at of canCut ? cutPositions : []) {
            if (best.count >= upperBound || at > height / 2) break
            if (at === 0) continue
            const count = solve(width, at).count + solve(width, height - at).count
            if (count > best.count) best = { count, kind: 'horizontal-cut', at }
        }

        memo.set(key, best)

        return best
    }

    const placements: FramePlacement[] = []

    const place = (width: number, height: number, originX: number, originY: number) => {
        const node = solve(width, height)

        if (node.kind === 'grid') {
            const columns = Math.floor(floorToPosition(cutPositions, width) / node.orientation.width)
            const rows = Math.floor(floorToPosition(cutPositions, height) / node.orientation.height)
            for (let row = 0; row < rows; row++) {
                for (let column = 0; column < columns; column++) {
                    placements.push({
                        x: toCm(originX + column * node.orientation.width),
                        y: toCm(originY + row * node.orientation.height),
                        rotated: node.orientation.rotated,
                    })
                }
            }
        } else if (node.kind === 'vertical-cut') {
            place(node.at, height, originX, originY)
            place(width - node.at, height, originX + node.at, originY)
        } else if (node.kind === 'horizontal-cut') {
            place(width, node.at, originX, originY)
            place(width, height - node.at, originX, originY + node.at)
        }
    }

    place(sheetWidth, sheetHeight, 0, 0)

    const verticalCount = placements.filter((placement) => placement.rotated).length

    return {
        count: placements.length,
        horizontalCount: placements.length - verticalCount,
        verticalCount,
        cellWidth: toCm(cellWidth),
        cellHeight: toCm(cellHeight),
        placements,
        isExhaustive: isExhaustive && canCut,
    }
}

export const roundUpToMultiple = (value: number, multiple: number): number => {
    if (!Number.isFinite(value)) return 0
    if (!(multiple > 0)) return value

    return Math.ceil(Number((value / multiple).toFixed(9))) * multiple
}
